import { SidUserClient } from "@sid/sid_client";
import { BaseInfo, ChoiceInfo, CommonAttrInfo, LinkageUserData, ManageInfo, MemberAgreementInfo, ShippingInfo, SiteInfo, UserBase, UserSpur } from "@/commons/interfaces";
import { InjectionKey, reactive } from "vue";
import { LinkageUserItem } from "@/modules/linkage/linkageUserItem";
import SpurProfile from "@/components/Linkage/Sites/SpurProfile.vue";
import SpurConfirm from "@/components/Linkage/Sites/SpurConfirm.vue";
// import SpurLoginCustom from "@/components/Linkage/Sites/SpurLoginCustom.vue";

// SPUR関連定数定義
/** 連携ID */
export const SPUR_LINKAGE_ID = 16;
/** 連携名称 */
export const SPUR_LINKAGE_NAME = "Spur";
/** 連携パス */
export const SPUR_PATH = SPUR_LINKAGE_NAME.toLowerCase();
/** サイト固有情報APIパス */
export const SPUR_SITE_UNIQ_API_PATH = "/spur/me";
/** デフォルトコールバックURL */
export const SPUR_DEFAULT_CALLBACK_URL = process.env.VUE_APP_SPUR_DEFAULT_CALLBACK_URL;
/** 許容コールバックURL */
export const SPUR_ACCEPT_CALLBACK_URLS = process.env.VUE_APP_SPUR_ACCEPT_CALLBACK_URLS.split(",").map((url: string) => `^${url}(\\?.*)?$`);
/** サービス規約URL */
export const SPUR_SERVICE_TERM_URL = process.env.VUE_APP_SPUR_SERVICE_TERM_URL;
/** メールアドレスドメイン */
export const SPUR_EMAIL_DOMAINS = [
    "spur.sid.shueisha.co.jp",
];
/** 基本属性情報入力 */
export const SPUR_PROFILE_COMPONENT = SpurProfile;
/** 基本属性情報確認 */
export const SPUR_CONFIRM_COMPONENT = SpurConfirm;
// /** ログイン画面カスタムパーツ */
// export const SPUR_LOGIN_CUSTOM_COMPONENT = SpurLoginCustom;

/**
 * SPUR連携画面用基本属性情報管理クラス
 */
export class SpurUserBase implements LinkageUserData<UserBase> {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<LinkageUserItem<LinkageUserData<UserBase>>> = Symbol("SpurUserBase");

    /** 基本属性情報 */
    public readonly data: UserBase = reactive(new UserBase());
    /**
     * @inheritDoc LinkageUserData
     */
    public initData(): void {
        this.copy(new UserBase());
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public getData(): UserBase {
        return this.data;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public copy(obj: UserBase): void {
        this.data.identityId = obj.identityId;
        this.data.userName = obj.userName;
        this.data.baseInfo = Object.assign(new BaseInfo(), obj.baseInfo);
        this.data.shippingInfo = obj.shippingInfo?.map(inf => Object.assign(new ShippingInfo(), inf)) || [];
        this.data.siteInfo = obj.siteInfo?.map(inf => Object.assign(new SiteInfo(), inf)) || [];
        this.data.commonAttrInfo = Object.assign(new CommonAttrInfo(), obj.commonAttrInfo);
        this.data.memberAgreeInfo = Object.assign(new MemberAgreementInfo(), obj.memberAgreeInfo);
        this.data.manageInfo = Object.assign(new ManageInfo(), obj.manageInfo);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public async fetch(sidClient: SidUserClient): Promise<void> {
        const response = await sidClient.getUserAttr()
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserBase();   
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.copy(response);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public checkRequired(): boolean {
        // 基本属性情報の有無
        if (!this.data.identityId) return false;
        // 登録者情報
        if (!this.data.baseInfo.firstName) return false;
        if (!this.data.baseInfo.lastName) return false;
        if (!this.data.baseInfo.firstKana) return false;
        if (!this.data.baseInfo.lastKana) return false;
        // if (!this.data.baseInfo.zipCode) return false;
        // if (!this.data.baseInfo.prefecture) return false;
        // if (!this.data.baseInfo.address) return false;
        // if (!this.data.baseInfo.building) return false;
        if (!this.data.baseInfo.email) return false;
        // if (!this.data.baseInfo.tel) return false;
        // 属性情報
        if (!this.data.commonAttrInfo.gender) return false;
        if (!this.data.commonAttrInfo.birthday) return false;
        // if (!this.data.commonAttrInfo.imagePath) return false;
        if (!this.data.commonAttrInfo.nickName) return false;
        // if (this.data.commonAttrInfo.isMarried === null || this.data.commonAttrInfo.isMarried === undefined) return false;
        // if (this.data.commonAttrInfo.hasChild === null || this.data.commonAttrInfo.hasChild === undefined) return false;
        // if (!this.data.commonAttrInfo.bloodType) return false;
        return true;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    getInjectKey(): InjectionKey<LinkageUserItem<LinkageUserData<UserBase>>> {
        return SpurUserBase.injectionKey;
    }

}

/**
 * SPUR連携画面用サイト固有情報管理クラス
 */
export class SpurSiteUniq implements LinkageUserData<UserSpur> {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<LinkageUserItem<LinkageUserData<UserSpur>>> = Symbol("UserSpur");

    /** SPURサイト固有情報 */
    public readonly data: UserSpur = reactive(new UserSpur());

    /**
     * @inheritDoc LinkageUserData
     */
    public initData(): void {
        this.copy(new UserSpur());
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public getData(): UserSpur {
        return this.data;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public copy(obj: UserSpur): void {
        this.data.identityId = obj.identityId;
        this.data.userName = obj.userName;
        this.data.job = obj.job;
        this.data.businessType = obj.businessType;
        this.data.isSendMailMagazine = obj.isSendMailMagazine;
        this.data.manageInfo = Object.assign({}, obj.manageInfo);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public async fetch(sidClient: SidUserClient): Promise<void> {
        const response = await sidClient.requestApi({ method: "GET", path: SPUR_SITE_UNIQ_API_PATH })
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserSpur();   
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.copy(response);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public checkRequired(): boolean {
        // サイト固有情報の有無
        if (this.data.job === null || this.data.job === undefined) return false;
        if (this.data.businessType === null || this.data.businessType === undefined) return false;
        return true;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    getInjectKey(): InjectionKey<LinkageUserItem<LinkageUserData<UserSpur>>> {
        return SpurSiteUniq.injectionKey;
    }
}

export const JOB_LIST = [
    "公務員",
    "経営者・役員",
    "会社員(事務系)",
    "会社員(技術系)",
    "会社員(その他)",
    "自営業",
    "自由業",
    "専業主婦(主夫)",
    "パート・アルバイト",
    "小学生",
    "中学生",
    "高校生",
    "専門学校生",
    "短期大学生",
    "大学・大学院生",
    "その他",
    "無職"
];

export const BUSINESS_TYPE_LIST = [
    "農業・林業・漁業・鉱業",
    "建設業",
    "製造業",
    "鉄鋼業",
    "出版・印刷関連産業",
    "電気・ガス・熱供給・水道業",
    "運送・運送業",
    "旅行業",
    "電気通信業",
    "卸売業・小売り",
    "飲食店",
    "金融業",
    "保険業",
    "不動産業",
    "サービス業（旅館・その他の宿泊所・娯楽・その他）",
    "放送業",
    "ソフトウェア・情報サービス業",
    "調査・広告代理業/医療業",
    "協同組合・教育関連・公務員",
    "勤めていない(専業主婦・学生などを含む)",
    "その他"
];
